import {
  convertLabToLch,
  convertLchToLab,
  convertOklabToRgb,
  convertRgbToLrgb,
  convertRgbToOklab,
  formatHex,
  type Lrgb,
  type Oklch,
  parseHex,
  type Rgb,
  wcagContrast,
} from "culori/fn";

const WHITE_LRGB = convertRgbToLrgb({ r: 1, g: 1, b: 1 });
const BLACK_LRGB = convertRgbToLrgb({ r: 0, g: 0, b: 0 });

const convertRgbToOklch = (input: Rgb): Oklch => {
  return convertLabToLch(convertRgbToOklab(input), "oklch");
};

const convertOklchToRgb = (input: Oklch): Rgb => {
  return convertOklabToRgb(convertLchToLab(input));
};

const isInGamut = (rgb: Rgb): boolean => {
  return (
    rgb.r >= 0 &&
    rgb.r <= 1 &&
    rgb.g >= 0 &&
    rgb.g <= 1 &&
    rgb.b >= 0 &&
    rgb.b <= 1
  );
};

/**
 * Core algorithm: adjusts a color's lightness to meet minimum contrast
 * against a reference color. Preserves hue and chroma when possible.
 */
const adjustLightnessForContrast = (
  colorToAdjust: Rgb,
  referenceLrgb: Lrgb,
  minContrast: number,
  preferLighten: boolean
): string | undefined => {
  const oklch = convertRgbToOklch(colorToAdjust);
  let { c, h } = oklch;

  const targetL = preferLighten ? 1 : 0;

  for (let chromaStep = 0; chromaStep <= 10; chromaStep++) {
    const testChroma = c * (1 - chromaStep * 0.1);

    let minL = 0;
    let maxL = 1;
    let foundL: number | undefined;

    for (let i = 0; i < 20; i++) {
      const testL = (minL + maxL) / 2;
      const testOklch: Oklch = { mode: "oklch", l: testL, c: testChroma, h };
      const testRgb = convertOklchToRgb(testOklch);

      if (!isInGamut(testRgb)) {
        if (targetL > testL) {
          minL = testL;
        } else {
          maxL = testL;
        }
        continue;
      }

      const testLrgb = convertRgbToLrgb(testRgb);
      const contrast = wcagContrast(testLrgb, referenceLrgb);

      if (contrast >= minContrast) {
        foundL = testL;
        // Move closer to original color
        if (targetL > testL) {
          maxL = testL;
        } else {
          minL = testL;
        }
      } else {
        // Need more contrast, move toward target
        if (targetL > testL) {
          minL = testL;
        } else {
          maxL = testL;
        }
      }
    }

    if (foundL !== undefined) {
      const resultRgb = convertOklchToRgb({
        mode: "oklch",
        l: foundL,
        c: testChroma,
        h,
      });
      return formatHex(resultRgb);
    }
  }

  return undefined;
};

/**
 * Returns "black" or "white" based on which provides better contrast
 * against the given background color. Favors white unless black has
 * significantly more contrast (controlled by whiteBias multiplier).
 */
export const getContrastingForeground = (
  bgHex: string,
  whiteBias: number = 1
): "black" | "white" | undefined => {
  const rgb = parseHex(bgHex);
  if (!rgb) {
    return undefined;
  }

  const lrgb = convertRgbToLrgb(rgb);
  const whiteContrast = wcagContrast(WHITE_LRGB, lrgb);
  const blackContrast = wcagContrast(BLACK_LRGB, lrgb);

  return blackContrast > whiteContrast * whiteBias ? "black" : "white";
};

/**
 * Returns a text color and potentially adjusted background to ensure
 * minimum contrast. Only adjusts the background if neither black nor
 * white meets the minimum contrast ratio.
 * Returns undefined if the color cannot be parsed.
 */
export const getAccessibleColorPair = (
  bgHex: string,
  minContrast: number = 4.5
): { text: "black" | "white"; background: string } | undefined => {
  const rgb = parseHex(bgHex);
  if (!rgb) {
    return undefined;
  }

  const lrgb = convertRgbToLrgb(rgb);
  const whiteContrast = wcagContrast(WHITE_LRGB, lrgb);
  const blackContrast = wcagContrast(BLACK_LRGB, lrgb);

  const useWhite = whiteContrast > blackContrast;
  const bestContrast = useWhite ? whiteContrast : blackContrast;

  if (bestContrast >= minContrast) {
    return { text: useWhite ? "white" : "black", background: bgHex };
  }

  // Adjust background: darken for white text, lighten for black text
  const textLrgb = useWhite ? WHITE_LRGB : BLACK_LRGB;
  const adjusted = adjustLightnessForContrast(
    rgb,
    textLrgb,
    minContrast,
    !useWhite
  );

  return {
    text: useWhite ? "white" : "black",
    background: adjusted ?? bgHex,
  };
};

/**
 * Adjusts the foreground color's lightness to meet the minimum contrast
 * ratio against the background color while preserving hue and chroma.
 * Returns undefined if the colors cannot be parsed.
 */
export const adjustColorForContrast = (
  fgHex: string,
  bgHex: string,
  minContrast: number = 2
): string | undefined => {
  const fgRgb = parseHex(fgHex);
  const bgRgb = parseHex(bgHex);

  if (!fgRgb || !bgRgb) {
    return undefined;
  }

  const fgLrgb = convertRgbToLrgb(fgRgb);
  const bgLrgb = convertRgbToLrgb(bgRgb);

  const currentContrast = wcagContrast(fgLrgb, bgLrgb);
  if (currentContrast >= minContrast) {
    return fgHex;
  }

  const bgOklch = convertRgbToOklch(bgRgb);
  const preferLighten = bgOklch.l < 0.5;

  return adjustLightnessForContrast(fgRgb, bgLrgb, minContrast, preferLighten);
};
